// components/ImageImport/ImageImportTool.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { loadModules } from 'esri-loader';
import {
  Upload,
  X,
  Target,
  Check,
  RotateCcw,
  RotateCw,
  Trash2,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import * as S from './imageImport.styles';
import { useImageStore, imageStoreHelpers } from '../../../../../store/imageStore';

const ImageImportTool = ({ view, isActive, onToggle }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showImageDropdown, setShowImageDropdown] = useState(false);
  const [needsImageReload, setNeedsImageReload] = useState(false);

  // Zustand store
  const {
    imageFile,
    imageUrl,
    opacity,
    rotation,
    extent,
    isImageLoaded,
    imageData,
    setImageFile,
    setImageUrl,
    setOpacity,
    setRotation,
    setExtent,
    setImageData,
    resetImage,
    updateImageSettings,
  } = useImageStore();

  // Refs
  const mediaLayerRef = useRef(null);
  const imageElementRef = useRef(null);
  const graphicsLayerRef = useRef(null);
  const sketchViewModelRef = useRef(null);
  const editGraphicRef = useRef(null);
  const fileInputRef = useRef(null);
  const currentExtentRef = useRef(null);
  const isUpdatingRef = useRef(false);

  // Initialize tool when activated
  useEffect(() => {
    if (isActive && view) {
      initializeTool();
    } else if (!isActive && view) {
      cleanup();
    }
    return () => {
      if (view) cleanup();
    };
  }, [isActive, view]);

  // Check for stored image configuration on mount
  useEffect(() => {
    if (isActive && !imageFile && !imageUrl) {
      checkForStoredImageConfig();
    }
  }, [isActive]);

  // Restore image from store when component becomes active or when imageUrl changes
  useEffect(() => {
    if (isActive && view && (imageUrl || imageData) && !imageElementRef.current) {
      restoreImageToMap();
    }
  }, [isActive, view, imageUrl, imageData]);

  // Update opacity when it changes
  useEffect(() => {
    if (imageElementRef.current && imageElementRef.current.opacity !== opacity) {
      imageElementRef.current.opacity = opacity;
    }
  }, [opacity]);

  // Update rotation when it changes (but not when we're updating from graphic manipulation)
  useEffect(() => {
    if (imageElementRef.current && currentExtentRef.current && !isUpdatingRef.current) {
      updateImageGeoreference();
      // Also update the edit graphic if we're currently editing
      if (isEditing && editGraphicRef.current && !isUpdatingRef.current) {
        updateEditGraphicRotation();
      }
    }
  }, [rotation, isEditing]);

  const checkForStoredImageConfig = useCallback(() => {
    if (imageStoreHelpers.hasStoredImageConfig()) {
      const metadata = imageStoreHelpers.getStoredImageMetadata();
      if (metadata) {
        setNeedsImageReload(true);
      }
    }
  }, []);

  const initializeTool = async () => {
    try {
      const [MediaLayer, GraphicsLayer] = await loadModules([
        'esri/layers/MediaLayer',
        'esri/layers/GraphicsLayer',
      ]);

      // Create MediaLayer if it doesn't exist
      if (!mediaLayerRef.current) {
        mediaLayerRef.current = new MediaLayer({
          title: 'Image Overlay',
          opacity: 1.0,
        });
        view.map.add(mediaLayerRef.current);
      }

      // Create GraphicsLayer for editing handles
      if (!graphicsLayerRef.current) {
        graphicsLayerRef.current = new GraphicsLayer({
          title: 'Image Editor',
          listMode: 'hide',
        });
        view.map.add(graphicsLayerRef.current);
      }

      await initializeSketchViewModel();

      // Restore image if it exists in store
      if ((imageUrl || imageData) && !imageElementRef.current) {
        await restoreImageToMap();
      }
    } catch (error) {
      console.error('Error initializing tool:', error);
    }
  };

  const initializeSketchViewModel = async () => {
    try {
      const [SketchViewModel] = await loadModules(['esri/widgets/Sketch/SketchViewModel']);

      if (!sketchViewModelRef.current) {
        sketchViewModelRef.current = new SketchViewModel({
          view,
          layer: graphicsLayerRef.current,
          updateOnGraphicClick: false,
          defaultUpdateOptions: {
            tool: 'transform',
            enableRotation: true,
            enableScaling: true,
            multipleSelectionEnabled: false,
            preserveAspectRatio: false,
            toggleToolOnClick: false,
          },
        });

        // Listen for update events to sync image position and scale
        sketchViewModelRef.current.on('update', (event) => {
          if (event.graphics?.[0] && imageElementRef.current && event.state === 'active') {
            isUpdatingRef.current = true;
            updateImageFromGraphic(event.graphics[0]);
            setTimeout(() => {
              isUpdatingRef.current = false;
            }, 100);
          }
        });
      }
    } catch (error) {
      console.error('Error initializing SketchViewModel:', error);
    }
  };

  const restoreImageToMap = async () => {
    if ((!imageUrl && !imageData) || !view || !mediaLayerRef.current || imageElementRef.current)
      return;

    try {
      setIsLoading(true);

      // Determine image source - prefer current imageUrl, fallback to stored base64 data
      let imageSource = imageUrl;
      if (!imageSource && imageData) {
        imageSource = imageData;
      }

      if (!imageSource) {
        setIsLoading(false);
        return;
      }

      // Use stored extent if available, otherwise calculate default
      let imageExtent = extent;
      if (!imageExtent) {
        const viewCenter = view.center;
        const viewExtent = view.extent;
        const size = Math.min(viewExtent.width, viewExtent.height) * 0.2;

        const [Extent] = await loadModules(['esri/geometry/Extent']);
        imageExtent = new Extent({
          xmin: viewCenter.x - size / 2,
          ymin: viewCenter.y - size / 2,
          xmax: viewCenter.x + size / 2,
          ymax: viewCenter.y + size / 2,
          spatialReference: view.spatialReference,
        });

        // Save the default extent to store
        setExtent(imageExtent);
      }

      await addImageToMapWithExtent(imageSource, imageExtent);
      setIsLoading(false);
      setNeedsImageReload(false);
    } catch (error) {
      console.error('Error restoring image to map:', error);
      setIsLoading(false);
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setIsLoading(true);

      try {
        // Clean up previous image properly
        await removeCurrentImage();

        // Convert file to base64 for storage
        const base64Data = await convertFileToBase64(file);
        const url = URL.createObjectURL(file);

        // Update store with new image data
        setImageFile(file);
        setImageUrl(url);
        setImageData(base64Data);
        setRotation(0);
        setExtent(null);

        // Load image to get dimensions
        const img = new Image();
        img.onload = async () => {
          await addImageToMap(url);
          setIsLoading(false);
        };
        img.onerror = () => {
          console.error('Failed to load image');
          setIsLoading(false);
        };
        img.src = url;
      } catch (error) {
        console.error('Error processing file:', error);
        setIsLoading(false);
      }
      setShowImageDropdown(false);
    }
  };

  const handleReloadImage = async () => {
    if (!needsImageReload) return;

    const metadata = imageStoreHelpers.getStoredImageMetadata();
    if (metadata) {
      if (fileInputRef?.current) {
        fileInputRef.current.click();
      }
    }
  };

  const handleUploadClick = () => {
    if (fileInputRef?.current) {
      fileInputRef.current.click();
    }
  };

  const removeCurrentImage = async () => {
    // Stop editing if active
    if (isEditing) {
      stopEditing();
    }

    // Clear all graphics layers
    if (graphicsLayerRef.current) {
      graphicsLayerRef.current.removeAll();
    }

    // Properly remove all elements from MediaLayer source
    if (mediaLayerRef.current && mediaLayerRef.current.source) {
      mediaLayerRef.current.source.elements.removeAll();
    }

    // Reset refs
    imageElementRef.current = null;
    editGraphicRef.current = null;
    currentExtentRef.current = null;
  };

  const addImageToMap = async (imageUrl) => {
    // Calculate extent based on current view
    const viewCenter = view.center;
    const viewExtent = view.extent;
    const size = Math.min(viewExtent.width, viewExtent.height) * 0.2;

    const [Extent] = await loadModules(['esri/geometry/Extent']);
    const extent = new Extent({
      xmin: viewCenter.x - size / 2,
      ymin: viewCenter.y - size / 2,
      xmax: viewCenter.x + size / 2,
      ymax: viewCenter.y + size / 2,
      spatialReference: view.spatialReference,
    });

    // Save extent to store
    setExtent(extent);

    await addImageToMapWithExtent(imageUrl, extent);
  };

  const addImageToMapWithExtent = async (imageSource, extent) => {
    if (!view || !mediaLayerRef.current) return;

    try {
      const [ImageElement, ExtentAndRotationGeoreference] = await loadModules([
        'esri/layers/support/ImageElement',
        'esri/layers/support/ExtentAndRotationGeoreference',
      ]);

      currentExtentRef.current = extent;

      // Create image element with proper rotation support
      const imageElement = new ImageElement({
        image: imageSource,
        opacity: opacity,
        georeference: new ExtentAndRotationGeoreference({
          extent: extent,
          rotation: rotation,
        }),
      });

      // Clear existing elements and add new one
      mediaLayerRef.current.source.elements.removeAll();
      mediaLayerRef.current.source.elements.add(imageElement);
      imageElementRef.current = imageElement;

      // Create a single, clean edit graphic
      await createEditGraphic(extent);
    } catch (error) {
      console.error('Error adding image to map:', error);
    }
  };

  const createEditGraphic = async (extent) => {
    if (!graphicsLayerRef.current) return;

    try {
      const [Graphic, SimpleFillSymbol, Polygon] = await loadModules([
        'esri/Graphic',
        'esri/symbols/SimpleFillSymbol',
        'esri/geometry/Polygon',
      ]);

      // Clear any existing graphics first
      graphicsLayerRef.current.removeAll();

      // Create a simple rectangle polygon matching the image extent
      const polygon = new Polygon({
        rings: [
          [
            [extent.xmin, extent.ymin],
            [extent.xmax, extent.ymin],
            [extent.xmax, extent.ymax],
            [extent.xmin, extent.ymax],
            [extent.xmin, extent.ymin], // Close the ring
          ],
        ],
        spatialReference: view.spatialReference,
      });

      const symbol = new SimpleFillSymbol({
        color: [0, 0, 0, 0], // Transparent fill
        outline: {
          color: [0, 150, 255, 1],
          width: 2,
          style: 'solid',
        },
      });

      editGraphicRef.current = new Graphic({
        geometry: polygon,
        symbol: symbol,
      });

      // Don't add to layer yet - only add when editing starts
    } catch (error) {
      console.error('Error creating edit graphic:', error);
    }
  };

  const updateEditGraphicRotation = async () => {
    if (!editGraphicRef.current || !currentExtentRef.current) return;

    try {
      const [Polygon] = await loadModules(['esri/geometry/Polygon']);

      const extent = currentExtentRef.current;
      const centerX = extent.xmin + extent.width / 2;
      const centerY = extent.ymin + extent.height / 2;
      const halfWidth = extent.width / 2;
      const halfHeight = extent.height / 2;

      const rotationRad = (rotation * Math.PI) / 180;
      const cos = Math.cos(rotationRad);
      const sin = Math.sin(rotationRad);

      // Calculate rotated corners
      const corners = [
        [-halfWidth, -halfHeight], // bottom-left
        [halfWidth, -halfHeight], // bottom-right
        [halfWidth, halfHeight], // top-right
        [-halfWidth, halfHeight], // top-left
      ].map(([x, y]) => {
        // Rotate around origin, then translate to center
        const rotatedX = x * cos - y * sin + centerX;
        const rotatedY = x * sin + y * cos + centerY;
        return [rotatedX, rotatedY];
      });

      // Close the polygon
      corners.push(corners[0]);

      const polygon = new Polygon({
        rings: [corners],
        spatialReference: view.spatialReference,
      });

      editGraphicRef.current.geometry = polygon;
    } catch (error) {
      console.error('Error updating edit graphic rotation:', error);
    }
  };

  const updateImageGeoreference = async () => {
    if (!imageElementRef.current || !currentExtentRef.current) return;

    try {
      const [ExtentAndRotationGeoreference] = await loadModules([
        'esri/layers/support/ExtentAndRotationGeoreference',
      ]);

      // Create new georeference with updated rotation
      const newGeoreference = new ExtentAndRotationGeoreference({
        extent: currentExtentRef.current,
        rotation: rotation,
      });

      // Update the image element's georeference
      imageElementRef.current.georeference = newGeoreference;
    } catch (error) {
      console.error('Error updating image georeference:', error);
    }
  };

  const updateImageFromGraphic = async (graphic) => {
    if (!imageElementRef.current || !graphic.geometry || !currentExtentRef.current) return;

    try {
      const [ExtentAndRotationGeoreference, Extent] = await loadModules([
        'esri/layers/support/ExtentAndRotationGeoreference',
        'esri/geometry/Extent',
      ]);

      // Calculate rotation from the polygon geometry
      const rings = graphic.geometry.rings[0];
      if (rings && rings.length >= 4) {
        const corners = rings.slice(0, 4); // Get the four corners

        // Calculate the rotation angle from the first edge
        // Vector from bottom-left to bottom-right corner
        const dx = corners[1][0] - corners[0][0];
        const dy = corners[1][1] - corners[0][1];

        // Calculate angle in degrees
        let rotationAngle = Math.atan2(dy, dx) * (180 / Math.PI);

        // Normalize to 0-360 range
        rotationAngle = (rotationAngle + 360) % 360;

        // Update rotation in store
        setRotation(rotationAngle);

        // Calculate the center point of the polygon
        const centerX = corners.reduce((sum, point) => sum + point[0], 0) / 4;
        const centerY = corners.reduce((sum, point) => sum + point[1], 0) / 4;

        // Calculate the width and height from the polygon edges
        const edge1X = corners[1][0] - corners[0][0];
        const edge1Y = corners[1][1] - corners[0][1];
        const width = Math.sqrt(edge1X * edge1X + edge1Y * edge1Y);

        const edge2X = corners[2][0] - corners[1][0];
        const edge2Y = corners[2][1] - corners[1][1];
        const height = Math.sqrt(edge2X * edge2X + edge2Y * edge2Y);

        // Create new extent based on the rotated polygon's dimensions
        const newExtent = new Extent({
          xmin: centerX - width / 2,
          ymin: centerY - height / 2,
          xmax: centerX + width / 2,
          ymax: centerY + height / 2,
          spatialReference: view.spatialReference,
        });

        // Update stored extent in store and ref
        currentExtentRef.current = newExtent;
        setExtent(newExtent);

        // Update the image element's georeference with new extent and calculated rotation
        const newGeoreference = new ExtentAndRotationGeoreference({
          extent: newExtent,
          rotation: rotationAngle,
        });

        imageElementRef.current.georeference = newGeoreference;
      }
    } catch (error) {
      console.error('Error updating image position:', error);
    }
  };

  const rotateImage = (degrees) => {
    const newRotation = (((rotation + degrees) % 360) + 360) % 360;
    setRotation(newRotation);
  };

  const handleRotationSliderChange = (value) => {
    setRotation(parseInt(value));
  };

  const handleResetRotation = () => {
    setRotation(0);
  };

  const startEditing = () => {
    if (!editGraphicRef.current || !sketchViewModelRef.current || !graphicsLayerRef.current) return;

    // Clear any existing graphics and add only our edit graphic
    graphicsLayerRef.current.removeAll();
    graphicsLayerRef.current.add(editGraphicRef.current);

    // Start the sketch update operation
    sketchViewModelRef.current.update([editGraphicRef.current], {
      tool: 'transform',
      enableRotation: true,
      enableScaling: true,
      preserveAspectRatio: false,
      multipleSelectionEnabled: false,
    });

    setIsEditing(true);
  };

  const stopEditing = () => {
    if (sketchViewModelRef.current) {
      sketchViewModelRef.current.cancel();
    }
    if (graphicsLayerRef.current) {
      graphicsLayerRef.current.removeAll();
    }
    setIsEditing(false);
  };

  const removeImage = async () => {
    await removeCurrentImage();

    // Reset store state
    resetImage();

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setShowImageDropdown(false);
    setNeedsImageReload(false);
  };

  const cleanup = () => {
    if (isEditing) {
      stopEditing();
    }

    // Clear all graphics
    if (graphicsLayerRef.current) {
      graphicsLayerRef.current.removeAll();
    }

    // Only remove layers if we're actually cleaning up (tool deactivated)
    if (!isActive) {
      if (mediaLayerRef.current && view) {
        if (mediaLayerRef.current.source) {
          mediaLayerRef.current.source.elements.removeAll();
        }
        view.map.remove(mediaLayerRef.current);
        mediaLayerRef.current = null;
      }

      if (graphicsLayerRef.current && view) {
        view.map.remove(graphicsLayerRef.current);
        graphicsLayerRef.current = null;
      }

      if (sketchViewModelRef.current) {
        sketchViewModelRef.current.destroy();
        sketchViewModelRef.current = null;
      }

      imageElementRef.current = null;
      editGraphicRef.current = null;
      currentExtentRef.current = null;
    }
  };

  const handleDropdownToggle = () => {
    setShowImageDropdown(!showImageDropdown);
  };

  if (!isActive) return null;

  // Get stored metadata for display
  const storedMetadata = imageStoreHelpers.getStoredImageMetadata();
  const hasStoredConfig = imageStoreHelpers.hasStoredImageConfig();

  return (
    <S.Panel className={isEditing ? 'editing-active' : ''}>
      {isLoading && (
        <S.LoadingOverlay>
          <S.LoadingSpinner />
        </S.LoadingOverlay>
      )}

      <S.Header>
        <S.Title>Image Import</S.Title>
        <S.CloseButton onClick={onToggle}>
          <X size={16} />
        </S.CloseButton>
      </S.Header>

      <S.UploadSection>
        {!imageFile && !needsImageReload ? (
          <>
            <S.UploadLabel>Choose Image File</S.UploadLabel>
            <S.HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              disabled={isLoading}
            />
            <S.UploadButton onClick={handleUploadClick} disabled={isLoading}>
              {isLoading ? <Loader2 size={24} className="animate-spin" /> : <Upload size={24} />}
              <div>
                <S.UploadButtonText>{isLoading ? 'Loading...' : 'Select Image'}</S.UploadButtonText>
              </div>
            </S.UploadButton>
            <S.UploadHelp>Supported: JPG, PNG, GIF, SVG</S.UploadHelp>
          </>
        ) : needsImageReload && !imageFile ? (
          <div
            style={{
              padding: '16px',
              textAlign: 'center',
              border: '2px dashed #ccc',
              borderRadius: '8px',
            }}
          >
            <ImageIcon size={48} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
            <div style={{ marginBottom: '12px' }}>
              <strong>Previous Image Configuration Found</strong>
              <br />
              <small style={{ opacity: 0.7 }}>
                {storedMetadata?.name} ({(storedMetadata?.size / 1024 / 1024)?.toFixed(2)} MB)
                <br />
                Rotation: {rotation}° | Opacity: {Math.round(opacity * 100)}%
              </small>
            </div>
            <S.HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              disabled={isLoading}
            />
            <S.UploadButton onClick={handleReloadImage} disabled={isLoading}>
              <RefreshCw size={20} />
              <S.UploadButtonText>Reload Image</S.UploadButtonText>
            </S.UploadButton>
            <div style={{ marginTop: '8px' }}>
              <button
                onClick={() => {
                  imageStoreHelpers.clearAllImageData();
                  setNeedsImageReload(false);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#666',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Clear stored configuration
              </button>
            </div>
          </div>
        ) : (
          <S.ImageDropdownContainer>
            <S.ImageDropdownTrigger onClick={handleDropdownToggle}>
              <S.ImagePreview>
                <S.PreviewImage src={imageUrl || imageData} alt="Loaded" />
                <S.ImageInfo>
                  <S.ImageName>
                    {imageFile?.name || storedMetadata?.name || 'Restored Image'}
                  </S.ImageName>
                  <S.ImageSize>
                    {imageFile
                      ? (imageFile.size / 1024 / 1024).toFixed(2) + ' MB'
                      : storedMetadata
                        ? (storedMetadata.size / 1024 / 1024).toFixed(2) + ' MB'
                        : 'Size unknown'}
                  </S.ImageSize>
                </S.ImageInfo>
              </S.ImagePreview>
              {showImageDropdown ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </S.ImageDropdownTrigger>

            {showImageDropdown && (
              <S.ImageDropdownMenu>
                <S.DropdownAction onClick={removeImage} className="remove">
                  <Trash2 size={16} />
                  Remove Image
                </S.DropdownAction>
              </S.ImageDropdownMenu>
            )}
          </S.ImageDropdownContainer>
        )}
      </S.UploadSection>

      {(imageFile || (needsImageReload && hasStoredConfig) || imageData) && (
        <S.ImageControls>
          <S.OpacityControl>
            <S.OpacityLabel>
              Opacity
              <S.OpacityValue>{Math.round(opacity * 100)}%</S.OpacityValue>
            </S.OpacityLabel>
            <S.OpacitySlider
              type="range"
              min="0.1"
              max="1"
              step="0.05"
              value={opacity}
              onChange={(e) => setOpacity(parseFloat(e.target.value))}
            />
          </S.OpacityControl>

          <S.RotationControl>
            <S.RotationLabel>
              Rotation
              <S.RotationValue>{Math.round(rotation)}°</S.RotationValue>
            </S.RotationLabel>
            <S.RotationButtons>
              <S.RotationButton onClick={() => rotateImage(-45)} title="Rotate -45°">
                -45°
              </S.RotationButton>
              <S.RotationButton onClick={() => rotateImage(-90)} title="Rotate -90°">
                <RotateCcw size={12} />
                -90°
              </S.RotationButton>
              <S.RotationButton
                onClick={handleResetRotation}
                className="reset"
                title="Reset rotation"
              >
                Reset
              </S.RotationButton>
              <S.RotationButton onClick={() => rotateImage(90)} title="Rotate +90°">
                <RotateCw size={12} />
                +90°
              </S.RotationButton>
              <S.RotationButton onClick={() => rotateImage(45)} title="Rotate +45°">
                +45°
              </S.RotationButton>
            </S.RotationButtons>
            <S.RotationFineControl>
              <S.RotationSliderLabel>Fine Rotation:</S.RotationSliderLabel>
              <S.RotationSlider
                type="range"
                min="0"
                max="359"
                step="1"
                value={rotation}
                onChange={(e) => handleRotationSliderChange(e.target.value)}
              />
            </S.RotationFineControl>
          </S.RotationControl>

          {imageElementRef.current && (
            <S.ActionButtonsGrid>
              <S.ActionButton
                onClick={isEditing ? stopEditing : startEditing}
                className={`edit-button ${isEditing ? 'active' : ''}`}
              >
                {isEditing ? (
                  <>
                    <Check size={16} />
                    Finish
                  </>
                ) : (
                  <>
                    <Target size={16} />
                    Transform
                  </>
                )}
              </S.ActionButton>
            </S.ActionButtonsGrid>
          )}
        </S.ImageControls>
      )}

      {!imageFile && !needsImageReload && !imageData && (
        <S.NoImageState>
          <ImageIcon size={48} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
          No image loaded.
          <br />
          Select an image file to get started.
        </S.NoImageState>
      )}
    </S.Panel>
  );
};

export default ImageImportTool;
